import { defineStore } from "pinia";

import { getCompanyId } from "@/utils/userUtils";

import fleetParksApi from "@/services/api/fleetParksApi";
import { useNotificationStore } from "@/stores/notificationStore";

export const useFleetParkStore = defineStore("fleetPark", {
  state: () => ({
    fleetParks: [],
    companyId: getCompanyId(),
    dataIsLoading: false,
    isSubmitting: false,
  }),

  getters: {
    fleetParkSelectOptions: (state) =>
      state.fleetParks
        .filter((park) => !park.archived_at)
        .map((park) => ({
          value: park.id,
          label: park.name,
        })),
  },

  actions: {
    async fetchFleetParks(params = {}) {
      this.dataIsLoading = true;

      try {
        const requestParams = { "q[archived]": "all", ...params };
        const firstResponse = await fleetParksApi.index(
          this.companyId,
          requestParams
        );
        const fleetParks = [...(firstResponse.data.result || [])];
        const pages = firstResponse.data.pagination_meta?.pages || 1;

        for (let page = 2; page <= pages; page += 1) {
          const response = await fleetParksApi.index(this.companyId, {
            ...requestParams,
            page,
          });
          fleetParks.push(...(response.data.result || []));
        }

        this.fleetParks = this.sortFleetParks(fleetParks);
      } finally {
        this.dataIsLoading = false;
      }
    },

    async createFleetPark(payload) {
      this.isSubmitting = true;

      try {
        const response = await fleetParksApi.create(
          this.companyId,
          payload
        );
        const fleetPark = response.data.result;

        await this.fetchFleetParks();
        useNotificationStore().showSuccess("Автопарк создан");

        return fleetPark;
      } finally {
        this.isSubmitting = false;
      }
    },

    async updateFleetPark(id, payload) {
      this.isSubmitting = true;

      try {
        const response = await fleetParksApi.update(
          this.companyId,
          id,
          payload
        );
        const fleetPark = response.data.result;

        await this.fetchFleetParks();
        useNotificationStore().showSuccess("Автопарк обновлен");

        return fleetPark;
      } finally {
        this.isSubmitting = false;
      }
    },

    async archiveFleetPark(id) {
      this.isSubmitting = true;

      try {
        await fleetParksApi.archive(this.companyId, id);
        await this.fetchFleetParks();
        useNotificationStore().showSuccess("Автопарк архивирован");
      } finally {
        this.isSubmitting = false;
      }
    },

    async restoreFleetPark(id) {
      this.isSubmitting = true;

      try {
        await fleetParksApi.restore(this.companyId, id);
        await this.fetchFleetParks();
        useNotificationStore().showSuccess("Автопарк восстановлен");
      } finally {
        this.isSubmitting = false;
      }
    },

    async reorderFleetParks(points) {
      const previousFleetParks = [...this.fleetParks];
      const positions = new Map(
        points.map(({ id, position }) => [String(id), position])
      );

      this.fleetParks = this.sortFleetParks(
        this.fleetParks.map((fleetPark) => ({
          ...fleetPark,
          position: positions.get(String(fleetPark.id)) ?? fleetPark.position,
        }))
      );

      try {
        await fleetParksApi.reorder(this.companyId, { points });
        useNotificationStore().showSuccess("Порядок автопарков обновлён");
      } catch (error) {
        this.fleetParks = previousFleetParks;
        useNotificationStore().showError("Не удалось изменить порядок автопарков");
        throw error;
      }
    },

    async assignVehicles(fleetParkId, vehicleIds) {
      const response = await fleetParksApi.assignVehicles(
        this.companyId,
        fleetParkId,
        { vehicle_ids: vehicleIds }
      );
      useNotificationStore().showSuccess("Машины добавлены в автопарк");

      return response.data.result;
    },

    sortFleetParks(fleetParks) {
      return [...fleetParks].sort((firstPark, secondPark) => {
        const firstPosition = Number(firstPark.position);
        const secondPosition = Number(secondPark.position);
        const firstHasPosition = Number.isFinite(firstPosition);
        const secondHasPosition = Number.isFinite(secondPosition);

        if (firstHasPosition && secondHasPosition) {
          return firstPosition - secondPosition;
        }
        if (firstHasPosition) return -1;
        if (secondHasPosition) return 1;

        return 0;
      });
    },
  },
});
